import { patch } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import {
  CaseStatus,
  type Patient,
  patientResponseSchema,
  type UpdatePatientRequest,
  updatePatientRequestSchema,
} from "../types";
import { getAgencyCasesQueryKey } from "./useAgencyCases";
import { getPatientQueryKey } from "./usePatient";

const PATIENT_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/:workplaceId/patients/:patientId`;

export function useUpdatePatient(
  patientId: string,
  options: UseMutationOptions<Patient, string, UpdatePatientRequest> = {}
) {
  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";

  return useMutation({
    mutationFn: async (params) => {
      const response = await patch({
        url: PATIENT_URL.replace(":workplaceId", workplaceId).replace(":patientId", patientId),
        data: params,
        requestSchema: updatePatientRequestSchema,
        responseSchema: patientResponseSchema,
      });
      return response.data.data;
    },
    onSuccess: () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries(
        getPatientQueryKey({
          workplaceId,
          patientId,
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries(
        getAgencyCasesQueryKey({
          workplaceId,
          filter: { status: CaseStatus.OPEN },
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries(
        getAgencyCasesQueryKey({
          workplaceId,
          filter: { status: CaseStatus.CLOSED },
        })
      );
    },
    ...options,
  });
}
